import { Typography, styled } from '@mui/material';

export const TEXT_SIZE = {
    XS: '12px', // new size based on design system
    SMALL: '12px',
    MEDIUM_SMALL: '14px',
    MEDIUM: '16px',
    LARGE_MEDIUM: '18px',
    LARGE: '24px',
    MEDIUM_LARGE: '30px',
    XLARGE: '32px',
    XXLARGE: '40px'
};

export const FONT_WEIGHT = {
    LIGHT: '300',
    REGULAR: '400',
    MEDIUM: '500',
    SEMI_BOLD: '600',
    BOLD: '700'
};

export const LINE_HEIGHT = {
    SMALL: '16px',
    MEDIUM_SMALL: '20px',
    MEDIUM: '24px',
    LARGE_MEDIUM: '28px',
    LARGE: '32px',
    XLARGE: '40px',
    XXLARGE: '48px'
};

export const SubHeading = styled(Typography)(({ theme }) => ({
    fontSize: '24px',
    fontWeight: '700'
}));

export const Heading = styled(Typography)(
    ({ fontSize, fontWeight, lineHeight }) => ({
        fontSize: (fontSize as string) ?? TEXT_SIZE.MEDIUM,
        fontWeight: (fontWeight as string) ?? FONT_WEIGHT.SEMI_BOLD,
        lineHeight: (lineHeight as string) ?? LINE_HEIGHT.MEDIUM
    })
);

export const Caption = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
    color: theme.palette.text.secondary
}));

export const SMBold = styled(Typography)(
    ({ theme, fontSize, color, fontWeight }) => ({
        fontSize: fontSize?.toString() ?? '12px',
        fontWeight: fontWeight?.toString() ?? '700',
        lineHeight: TEXT_SIZE.SMALL,
        color: color?.toString() ?? theme.palette.text.primary
    })
);

export const SMSemiBold = styled(SMBold)(({ fontWeight }) => ({
    fontWeight: fontWeight?.toString() ?? '600'
}));

export const XSMedium = styled(Typography)(({ theme, color }) => ({
    fontSize: TEXT_SIZE.XS,
    fontWeight: FONT_WEIGHT.MEDIUM,
    lineHeight: '18px',
    color: color?.toString()
}));

export const PageHeading = styled(Typography)(() => ({
    fontSize: '32px',
    fontWeight: '500',
    lineHeight: '40px',
    display: 'flex',
    alignItems: 'center'
}));

export const PageSubHeading = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '24px',
    color: theme.palette.text.secondary
}));

export const Title = styled(Typography)(
    ({ fontSize, fontWeight, lineHeight }) => ({
        fontSize: (fontSize as string) ?? TEXT_SIZE.MEDIUM_SMALL,
        fontWeight: (fontWeight as string) ?? FONT_WEIGHT.MEDIUM,
        lineHeight: (lineHeight as string) ?? LINE_HEIGHT.MEDIUM_SMALL
    })
);

export const SubTitle = styled(Typography)(
    ({ fontSize, fontWeight, lineHeight, color }) => ({
        fontSize: (fontSize as string) ?? TEXT_SIZE.MEDIUM_SMALL,
        fontWeight: (fontWeight as string) ?? FONT_WEIGHT.REGULAR,
        lineHeight: (lineHeight as string) ?? LINE_HEIGHT.MEDIUM_SMALL,
        color: color ?? '#667085'
    })
);

export const SemiBold = styled(Typography)`
    font-family: 'Inter';
    font-weight: 600;
`;
export const Regular = styled(Typography)`
    font-family: 'Inter';
    font-weight: 600;
    font-size: 12px;
`;
export const SMRegular = styled(Regular)`
    font-size: 12px;
    font-weight: 400;
    font-family: Inter;
    line-height: 16px;
    letter-spacing: 0%;
`;

export const MDRegular = styled(Regular)`
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 16.94px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

export const SMMedium = styled(Typography)`
    font-family: 'Inter';
    font-weight: 500;
    font-size: 14px;
`;

export const SMMedium700 = styled(Typography)`
    //styleName: Text sm/Medium;
    font-family: Inter;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

export const LGMedium = styled(Typography)`
    font-family: Inter;
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: 0%;
`;

export const TextMDSemiBold = styled(SemiBold)`
    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0%;
`;

export const TextDisplayMDSemiBold = styled(SemiBold)`
    font-family: Inter;
    font-weight: 600;
    font-size: 36px;
    line-height: 44px;
    letter-spacing: -2%;
`;

export const TextLGSemiBold = styled(SemiBold)`
    font-family: Inter;
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

export const TwoXRegular = styled(Regular)`
    font-family: Inter;
    font-size: 72px;
    font-weight: 400;
    line-height: 90px;
    letter-spacing: -0.02em;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

export const TextSMSemiBold = styled(SemiBold)(
    ({ fontWeight, fontSize, lineHeight }) => ({
        fontFamily: 'Inter',
        fontWeight: fontWeight?.toString() ?? '500',
        fontSize: fontSize?.toString() ?? '12px',
        lineHeight: lineHeight?.toString() ?? '16px',
        letterSpacing: '0%',
        verticalAlign: 'middle',
        padding: '0px'
    })
);

export const TextLGSemiBoldBrand = styled(Typography)`
    color: var(--Brand-25, #f5faff);
    font-family: Inter;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
`;

export const TextDisplaySMMedium = styled(Typography)`
    color: var(--Brand-25, #f5faff);
    font-family: Inter;
    font-size: 30px;
    font-style: normal;
    font-weight: 500;
    line-height: 38px;
`;

export const TextLGRegularBrand = styled(Typography)`
    color: var(--Brand-25, #f5faff);
    font-family: Inter;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
`;

export const TextLGRegular = styled(Typography)`
    color: var(--Brand-25, #f5faff);
    font-family: Inter;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
`;

export const TextMedium = styled(Typography)`
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
`;

export const TextSMRegular = styled(Typography)(
    ({ fontFamily, fontWeight, fontSize, letterSpacing }) => ({
        fontFamily: fontFamily?.toString() ?? 'Inter',
        fontWeight: fontWeight?.toString() ?? '400',
        fontSize: fontSize?.toString() ?? '14px',
        letterSpacing: letterSpacing?.toString() ?? '0%'
    })
);

export const TextMailParagraph = styled(Typography)`
    font-family: Arial;
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    vertical-align: middle;
`;
